/**
 * @module utils/abortController
 * @description Utilities for managing cancellable requests with AbortController
 *
 * Provides a pattern for preventing race conditions in async operations
 * by automatically cancelling previous in-flight requests when a new one
 * is initiated.
 *
 * @example
 * const requestController = createCancellableRequest();
 *
 * async function loadData() {
 *   const signal = requestController.getSignal(); // Cancels any previous request
 *   const result = await fetch('/api/data', { signal });
 *   if (signal.aborted) return; // Request was superseded
 *   // Process result...
 * }
 */

// =============================================================================
// TYPES
// =============================================================================

/**
 * Interface for a cancellable request controller
 */
export interface CancellableRequest {
  /**
   * Cancel the current in-flight request (if any)
   */
  cancel: () => void;

  /**
   * Get a new AbortSignal, automatically cancelling any previous request.
   * Call this at the start of each new request to ensure only the latest
   * request will update state.
   * @returns AbortSignal to pass to fetch or other async operations
   */
  getSignal: () => AbortSignal;

  /**
   * Check if there's currently an active (non-aborted) controller
   */
  isActive: () => boolean;
}

// =============================================================================
// FACTORY FUNCTION
// =============================================================================

/**
 * Creates a cancellable request controller for managing in-flight requests.
 *
 * This pattern prevents race conditions where an older, slower request
 * could overwrite data from a newer, faster request.
 *
 * @param debugName - Optional name for debug logging
 * @returns CancellableRequest controller
 *
 * @example
 * // In a store
 * const itemsRequest = createCancellableRequest('items');
 *
 * async function loadItems() {
 *   const signal = itemsRequest.getSignal();
 *
 *   const result = await api.getItems({ signal });
 *
 *   // Check if request was superseded by a newer one
 *   if (signal.aborted) {
 *     log.debug('Request aborted, ignoring result');
 *     return;
 *   }
 *
 *   // Safe to update state
 *   updateStore(result);
 * }
 */
export function createCancellableRequest(debugName?: string): CancellableRequest {
  let controller: AbortController | null = null;

  return {
    cancel: () => {
      if (controller) {
        controller.abort();
        controller = null;
      }
    },

    getSignal: () => {
      // Cancel any existing request
      if (controller) {
        controller.abort();
      }
      // Create new controller
      controller = new AbortController();
      return controller.signal;
    },

    isActive: () => {
      return controller !== null && !controller.signal.aborted;
    },
  };
}

// =============================================================================
// ERROR DETECTION
// =============================================================================

/**
 * Check if an error is an AbortError (request was cancelled)
 *
 * This is useful for silently ignoring errors from cancelled requests,
 * as these are expected when using the cancellable request pattern.
 *
 * @param error - The error to check
 * @returns true if the error indicates the request was aborted
 *
 * @example
 * try {
 *   await fetch('/api/data', { signal });
 * } catch (error) {
 *   if (isAbortError(error)) {
 *     // Expected - request was cancelled, do nothing
 *     return;
 *   }
 *   // Real error - handle it
 *   handleError(error);
 * }
 */
export function isAbortError(error: unknown): boolean {
  // Check for DOMException AbortError
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }

  // Check for Error with AbortError name
  if (error instanceof Error && error.name === 'AbortError') {
    return true;
  }

  // Check for string errors containing abort
  if (typeof error === 'string') {
    const lowerError = error.toLowerCase();
    return lowerError.includes('aborted') || lowerError.includes('abort');
  }

  return false;
}
